
import React, { useEffect, useState } from 'react';
import { SimulationConfig, MelodyName } from '@/types';
import { CloudService } from '@/services/CloudService';
import { Music, Radio, Disc } from 'lucide-react';
import { ControlGroup } from './ControlGroup';

interface MelodyLibrarySectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const MelodyLibrarySection: React.FC<MelodyLibrarySectionProps> = ({ config, setConfig }) => {
  const [customMelodies, setCustomMelodies] = useState<any[]>([]);

  useEffect(() => {
    CloudService.getMelodies().then(fetched => {
        const melodiesArray = Array.isArray(fetched) ? fetched : [];
        const allMelodies = [...melodiesArray, ...(config.customMelodies || [])];
        const uniqueMelodies = Array.from(new Map(allMelodies.map(m => [m.id, m])).values());
        setCustomMelodies(uniqueMelodies);
    });
  }, [config.customMelodies]);

  const melodies: MelodyName[] = [
    'Original Masterpiece',
    'Pentatonic', 'Megalovania', 'Mario', 'Imperial March', 'Tetris', 'Rickroll', 'Pokemon', 'Zelda', 'Nokia', 
    'Fur Elise', 'Canon in D', 'Mortal Kombat', 'Axel F', 'Pirates', 'Star Wars', 'Harry Potter', 'Minecraft', 
    'SpongeBob', 'Under the Sea', 'Circle of Life', 'Smooth Criminal', 'Billie Jean', 'Seven Nation Army', 
    'We Will Rock You', 'Sandstorm', 'Mission Impossible', 'James Bond', 'Nostalgia', 'Gravity Falls', 'Kerosene',
    'Interstellar', 'Inception', 'Stranger Things', 'Cyberpunk', 'Get Lucky', 'Blinding Lights', 'Take On Me',
    'Final Fantasy', 'Doom', 'Pac-Man', 'Sonic', 'Skyrim', 'Bad Apple', 'Rush E', 'Giorno',
    'One Summers Day', 'Merry-Go-Round', 'Unravel', 'Cruel Angel', 'Tokyo Ghoul',
    'Custom (Uploaded)'
  ];

  return (
    <div className={`p-8 rounded-[2.5rem] border shadow-sm transition-colors duration-300 ${config.darkMode ? 'bg-white/10 border-white/5' : 'bg-white border-zinc-100 shadow-sm'}`}>
      <div className="flex items-center gap-3 mb-8 px-1">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${config.darkMode ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/10 shadow-[0_0_15px_rgba(6,182,212,0.15)]' : 'bg-zinc-50 text-cyan-600 border border-zinc-100 shadow-sm'}`}>
          <Disc className="w-5 h-5" />
        </div>
        <h3 className="text-[12px] font-medium uppercase tracking-[0.3em] opacity-60">Sonic Library</h3>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <ControlGroup label="Selected Masterwork" settingKey="selectedMelody" darkMode={config.darkMode}>
          <select 
            value={config.selectedMelody} 
            onChange={e => setConfig(p => ({ ...p, selectedMelody: e.target.value as MelodyName }))} 
            className={`w-full h-12 px-5 rounded-2xl text-[10px] font-medium uppercase tracking-[0.2em] outline-none cursor-pointer border transition-all ${config.darkMode ? 'bg-black/30 border-white/5 text-white' : 'bg-zinc-50 border-zinc-100 text-zinc-950 hover:bg-white focus:bg-white focus:border-cyan-600/30 shadow-inner'}`}
          >
            <optgroup label="Standard Library" className={` ${config.darkMode ? 'bg-zinc-900 border-white/5 text-white' : 'bg-white border-zinc-100 text-zinc-950'}`}>
                {melodies.map(m => <option key={m} value={m}>{m}</option>)}
            </optgroup>
            {customMelodies.length > 0 && (
                <optgroup label="Custom Melodies (Cloud)" className={` ${config.darkMode ? 'bg-zinc-900 border-white/5 text-white' : 'bg-white border-zinc-100 text-zinc-950'}`}>
                    {customMelodies.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </optgroup>
            )}
          </select>
          </ControlGroup>
        </div>

        <div className={`flex items-start gap-4 p-5 rounded-2xl border transition-all ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50/50 border-zinc-100 shadow-sm'}`}>
          <div className={`p-3 rounded-xl transition-all ${config.darkMode ? 'bg-cyan-500/10 text-cyan-400' : 'bg-white text-cyan-600 border border-zinc-100'}`}>
            <Music className="w-4 h-4" />
          </div>
          <p className="text-[10px] font-medium text-zinc-400 leading-relaxed uppercase tracking-widest max-w-[200px]">
            The physics engine maps ball collisions to these musical parameters.
          </p>
        </div>
      </div>
    </div>
  );
};
